/**
 * Global safety net for htmx loading indicators.
 *
 * htmx shows a spinner by putting the `htmx-request` class on the requesting
 * element (or on the element named by hx-indicator) and removes it when the
 * request settles. That cleanup is skipped in a few real situations — most
 * often when the element that issued the request is removed from the DOM while
 * its request is still settling, which is exactly what a modal does when it
 * closes itself on success. The class is then left behind with nothing to clear
 * it, and the spinner turns forever next to a form that already finished.
 *
 * Rather than patch each flow, this tracks how many htmx requests are actually
 * in flight and, once that reaches zero, clears any `htmx-request` class still
 * hanging around. Because it only ever acts when nothing is loading, it cannot
 * hide a spinner that belongs to a live request — including concurrent ones.
 *
 * The CSS counterpart lives in static/css/src/main.css: `.htmx-indicator` is
 * hidden by default there, so indicators do not depend on the stylesheet htmx
 * injects at runtime.
 */

let inFlight = 0;

// Classes htmx applies for the duration of a request or swap. All of them are
// transient by definition, so none of them should survive an idle moment.
// htmx-swapping in particular is left behind whenever a swapped-in script
// empties its own target (an inline modal closing itself), which strands any
// style keyed on it.
const TRANSIENT_CLASSES = ['htmx-request', 'htmx-swapping', 'htmx-settling'];

function clearStaleIndicators() {
    if (inFlight > 0) {
        return;
    }
    TRANSIENT_CLASSES.forEach((className) => {
        document.querySelectorAll('.' + className).forEach((element) => {
            element.classList.remove(className);
        });
    });
}

export function init() {
    document.body.addEventListener('htmx:beforeRequest', () => {
        inFlight += 1;
    });

    ['htmx:afterRequest', 'htmx:responseError', 'htmx:sendError', 'htmx:timeout']
        .forEach((eventName) => {
            document.body.addEventListener(eventName, () => {
                inFlight = Math.max(0, inFlight - 1);
                // Settle first, so htmx's own cleanup runs before ours; ours
                // only picks up what it left behind.
                window.setTimeout(clearStaleIndicators, 0);
            });
        });

    // An aborted request never fires afterRequest in every htmx version.
    document.body.addEventListener('htmx:abort', () => {
        inFlight = Math.max(0, inFlight - 1);
        window.setTimeout(clearStaleIndicators, 0);
    });
}

export default { init };
